// Copyright (c) 2020-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { ReactNode } from "react";
import { ButtonStyle } from "../../../../io/hyperify/core/frontend/button/ButtonStyle";
import { map } from "../../../../io/hyperify/core/functions/map";
import { Product } from "../../../../io/hyperify/core/store/types/product/Product";
import {
    createProductModel,
    ProductModel,
} from "../../../../io/hyperify/core/store/types/product/ProductModel";
import { ProductPrice } from "../../../../io/hyperify/core/store/types/product/ProductPrice";
import { ProductPriceType } from "../../../../io/hyperify/core/store/types/product/ProductPriceType";
import { isString } from "../../../../io/hyperify/core/types/String";
import { TranslationFunction } from "../../../../io/hyperify/core/types/TranslationFunction";
import { TranslationParams } from "../../../../io/hyperify/core/types/TranslationParams";
import { SendanorProductUtils } from "../../core/utils/SendanorProductUtils";

/**
 *
 * @param productList
 * @param priceType
 * @param dateTimeFormat
 * @param getIcon
 * @param t
 * @param getRoute
 * @param getButtonLabel
 * @param getProductPrice
 * @param getButtonStyle
 * @param getTitle
 * @param getSummary
 */
export function useProductModelList (
    productList      : readonly Product[],
    priceType        : ProductPriceType,
    dateTimeFormat   : string,
    t                : TranslationFunction,
    getIcon          : (item: Product, price: ProductPrice | undefined, translationParams : TranslationParams) => any,
    getRoute         : (item: Product, price: ProductPrice | undefined, translationParams : TranslationParams) => (string | undefined),
    getButtonLabel   : (item: Product, price: ProductPrice | undefined, translationParams : TranslationParams) => (string | undefined),
    getProductPrice  : (item: Product, price: ProductPrice | undefined, translationParams : TranslationParams) => (ProductPrice | undefined),
    getButtonStyle   : (item: Product, price: ProductPrice | undefined, translationParams : TranslationParams) => (ButtonStyle | undefined),
    getTitle        ?: (item: Product, price: ProductPrice | undefined, translationParams : TranslationParams) => string,
    getSummary      ?: (item: Product, price: ProductPrice | undefined, translationParams : TranslationParams) => string | ReactNode,
) : ProductModel[] {
    return map(
        SendanorProductUtils.findProductsWithPriceType( productList, priceType ),
        ( item : Product ) : ProductModel => {
            const price : ProductPrice | undefined = SendanorProductUtils.findPrice( item, priceType );
            const translationParams : TranslationParams = SendanorProductUtils.createTranslationParamsFromPrice(
                price,
                dateTimeFormat,
                t,
            );
            const Icon = getIcon(item, price, translationParams);
            const title : string = (getTitle ?? defaultGetTitle)(item, price, translationParams);
            const summary : string | ReactNode = (getSummary ?? defaultGetSummary)(item, price, translationParams);
            const buttonLabel : string | undefined = getButtonLabel(item, price, translationParams);
            return createProductModel(
                item.id,
                <Icon />,
                t( title, translationParams ),
                isString(summary) ? t( summary, translationParams ) : summary,
                getRoute( item, price, translationParams ),
                buttonLabel ? t( buttonLabel, translationParams ) : undefined,
                item,
                getProductPrice( item, price, translationParams ),
                getButtonStyle( item, price, translationParams ),
            );
        }
    );
}

function defaultGetTitle (item: Product) : string {
    return item.title;
}

function defaultGetSummary (item: Product) : string {
    return item.summary;
}
